// frontend/src/stores/InvestigationStore.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import apiClient from "@/services/api";

const TTL = 5 * 60 * 1000; // 5 minutes

export const useInvestigationStore = defineStore("investigation", () => {
  // ---------- STATE ----------
  const investigations = ref(new Map()); // Map<token, Investigation>
  const lastFetch = ref(null);
  const isLoading = ref(false);
  const error = ref(null);
  let inFlight = null; // Promise of the running fetch, shared by concurrent callers

  // ---------- ACTIONS ----------
  function fetchInvestigations() {
    // Concurrent callers wait for the same request instead of skipping it
    if (!inFlight) {
      inFlight = doFetch().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  }

  async function doFetch() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await apiClient.getListInvestigation();

      if (response.data.result === "OK") {
        const parseDate = (dateStr) => {
          if (!dateStr) return null;

          const [day, month, year] = dateStr.split("/");

          // convert 2-digit year to 20xx (adjust if needed)
          const fullYear = year.length === 2 ? `20${year}` : year;

          return new Date(fullYear, month - 1, day);
        };

        const list = (response.data.message.investigations || []).sort(
          (a, b) => {
            const dateA = parseDate(a.publication_date);
            const dateB = parseDate(b.publication_date);

            if (!dateA) return 1;
            if (!dateB) return -1;

            return dateB - dateA;
          },
        );
        // Populate Map with token as key for O(1) lookup
        const newMap = new Map();
        for (const inv of list) {
          newMap.set(inv.id + "", inv);
        }

        investigations.value = newMap;
        lastFetch.value = Date.now();
      } else {
        throw new Error(response.data.message?.error || "Unknown error");
      }
    } catch (e) {
      error.value = e?.message || "Failed to fetch investigations";
      console.error("[InvestigationStore] Fetch error:", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function getByIdOrFetch(id) {
    id = String(id);
    // Refetch when empty, stale, or the ID is missing (e.g. newly published)
    if (isStale.value || !investigations.value.has(id)) {
      await fetchInvestigations();
    }
    console.log(
      `[InvestigationStore] getByIdOrFetch: looking for ID ${id} in store...`,
    );
    console.log(
      `[InvestigationStore] Current investigations in store: ${Array.from(
        investigations.value.keys(),
      ).join(", ")}`,
    );
    return investigations.value.get(id);
  }

  function refresh() {
    // Force refetch
    lastFetch.value = null;
    return fetchInvestigations();
  }

  function clear() {
    investigations.value.clear();
    lastFetch.value = null;
    error.value = null;
  }

  // ---------- GETTERS ----------
  const list = computed(() => Array.from(investigations.value.values()));

  const count = computed(() => investigations.value.size);

  const isStale = computed(() => {
    if (!lastFetch.value) return true;
    return Date.now() - lastFetch.value > TTL;
  });

  // ---------- API ----------
  return {
    // state
    isLoading,
    error,

    // actions
    fetchInvestigations,
    getByIdOrFetch,
    refresh,
    clear,

    // getters
    list,
    count,
    isStale,
  };
});
